import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames';
import Hint from './Hint';
import FragmentsWrapper from './FragmentsWrapper';
import Progress from './Progress';
import MetroWrapper from './MetroWrapper';
import Level from './Level';
import CloseButton from '../CloseButton';
import VolumeButton from '../Volume';
import RulesButton from '../RulesButton';

type TProgressBarProps = {
  time: number | undefined; // Время в секундах
  onEnd?: VoidFunction;
  onClose?: VoidFunction;
  isOnPause?: boolean;
  onRulesModalToggle?: (isOpen: boolean) => void;
  children?: ReactNode;
} & (
  | {
      variant: 'metro';
      onHintClick?: never;
      isHintVibrate?: never;
      hintCount?: never;
      currentLevel: number;
      totalLevels: number;
    }
  | {
      variant: 'fragments';
      onHintClick?: VoidFunction;
      isHintVibrate: boolean;
      hintCount: number;
      currentLevel?: never;
      totalLevels?: never;
    }
);

const ProgressBar: FC<TProgressBarProps> = ({
  time = 60,
  onEnd,
  hintCount,
  onClose,
  onHintClick,
  isHintVibrate,
  variant,
  isOnPause = false,
  currentLevel,
  totalLevels,
  onRulesModalToggle,
  children,
}) => {
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pauseTimeRef = useRef<number>(0);
  const totalTime = time * 1000;

  const updateProgress = () => {
    const elapsedTime = Date.now() - startTimeRef.current;
    const newProgress = 100 - (elapsedTime / totalTime) * 100;

    if (newProgress <= 0) {
      setProgress(0);
      onEnd && onEnd();
      stopInterval();
      return;
    }

    setProgress(newProgress);
  };

  const startInterval = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(updateProgress, 100);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!isOnPause) {
      startTimeRef.current = Date.now() - pauseTimeRef.current;
      startInterval();
    } else {
      stopInterval();
      pauseTimeRef.current = Date.now() - startTimeRef.current;
    }

    return () => {
      stopInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnPause]);

  useEffect(() => {
    if (!isOnPause) {
      startTimeRef.current = Date.now();
      startInterval();
    }

    return () => {
      stopInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenRulesModal = useCallback(() => {
    onRulesModalToggle?.(true);
  }, [onRulesModalToggle]);

  const handleCloseRulesModal = useCallback(() => {
    onRulesModalToggle?.(false);
  }, [onRulesModalToggle]);

  const renderWrapper = useMemo(() => {
    return (
      <>
        {variant === 'fragments' && (
          <FragmentsWrapper>
            <Progress progress={progress} />
          </FragmentsWrapper>
        )}

        {variant === 'metro' && (
          <MetroWrapper isPaused={isOnPause}>
            <Progress progress={progress} />
          </MetroWrapper>
        )}
      </>
    );
  }, [variant, progress, isOnPause]);

  return (
    <div className={classNames(styles.progressBar, { [styles[variant]]: Boolean(variant) }, 'progressBar')}>
      <div className={styles.leftButtons}>
        {variant === 'fragments' && <Hint className={styles.hint} count={hintCount} isVibrate={isHintVibrate} onClick={onHintClick} />}
        {variant === 'metro' && <Level className={styles.level} active={currentLevel} total={totalLevels} />}
      </div>

      <div className={styles.progressWrapper}>
        {renderWrapper}
        {children}
      </div>

      <div className={styles.rightButtons}>
        <RulesButton onOpenModal={handleOpenRulesModal} onCloseModal={handleCloseRulesModal} />
        <VolumeButton className={styles.volume} />

        <CloseButton
          className={styles.close}
          onClick={onClose}
          {...(variant === 'metro' && { size: 66, tabletSize: 48, iconSize: 25, tabletIconSize: 20 })}
          color="blue"
        />
      </div>
    </div>
  );
};

export default ProgressBar;
